/**
 * HUPALUPA Grubu — Faturalandırma (KOPYA — /grup/hupalupa/faturalandirma-kopya)
 * Asıl sayfa: ./HupalupaGroupBilling.jsx. Burada denenen değişiklikler oraya yansımaz.
 *
 * Patron paneliyle ({@link ./HupalupaGroup.jsx}) AYNI uçtan beslenir:
 * `GET /api/group/hupalupa/monthly`. Orada marka × mecra × ay harcaması zaten
 * canlı geliyor (`brands[].months[ay].platforms[MECRA].spend`); bu sayfa ona
 * fatura yüklerini bindirir. Yeni bir backend rotası YOK, yazılmasın —
 * harcamayı ikinci bir yerden çekmek iki sayfayı ayrı rakamlara düşürür.
 *
 * ── Fatura merdiveni (Efe'nin Excel'inden birebir, Temmuz 2026 ile doğrulandı)
 *   harcama            130.192
 *   × (1 + mecra ücreti)  → 136.051   Google %4,5 · Meta %5 · TikTok yok
 *   ÷ (1 − stopaj)        → 160.060   🔴 BRÜTLEŞTİRME, düz çarpım DEĞİL
 *   × (1 + KDV)           → 192.071
 *   Glowy hakediş %10     →  13.019   🔴 HAM harcama üzerinden, yüklerden önce
 *
 * 🔴🔴 Mecra ücreti stopaj matrahının İÇİNDE. Excel'de Google'ın %4,5'i
 * stopajdan önce biniyor (136.051 → 160.060); Meta'nın %5'i de aynı yere
 * konuldu ("google %4.5 diyor ya" — Efe, 1 Eyl 2026). Ücreti stopajdan sonra
 * eklersen Google sütunu Excel'le tutmaz.
 *
 * 🔴 Meta %5 konum ücreti **Temmuz 2026'dan itibaren** var (`META_FEE_BASLANGIC`).
 * Haziran ve öncesinde hücre "—" kalır; 0,00 yazmak "hesaplandı, sıfır çıktı"
 * demek olurdu. /faturalama sayfasında da aynı tarih kuralı var.
 *
 * ⚠️ Mecra API'lerinin `spend` alanı bu yüklerin HİÇBİRİNİ içermez (ne Google'ın
 * %4,5'i ne Meta'nın %5'i faturaya API'den gelir) — hepsi burada elle bindirilir.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Loader2, AlertTriangle, Printer, FileSpreadsheet, Info, ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx-js-style';

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const AY_ADLARI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/** Kaç ay geriye bakılabilsin — fatura kontrolü geçmişe dönük yapılıyor. */
const AY_SAYISI = 12;

/* ── Mecralar ──────────────────────────────────────────────────────────
   Anahtarlar backend'in `platforms` anahtarlarıyla aynı olmak ZORUNDA
   (groupPanel.js: META_ADS / GOOGLE_ADS / TIKTOK_ADS). Logolar /faturalama
   sayfasıyla ortak dosyalar. */
const MECRALAR = [
  { key: 'GOOGLE_ADS', label: 'Google Ads', logo: '/google_ads-512.webp', ucret: 'google' },
  { key: 'META_ADS', label: 'Meta', logo: '/6033716.png', ucret: 'meta' },
  { key: 'TIKTOK_ADS', label: 'TikTok', logo: '/tik-tokiconss.png', ucret: null },
];

/**
 * Varsayılan oranlar. Ekrandan değiştirilebilir ama KAYDEDİLMEZ — bu sayfa
 * kontrol ekranı, oran deposu değil. Kalıcı oran gerekiyorsa
 * /harcama-faturasi'nın `billing_fee_settings` tablosu var.
 */
const VARSAYILAN_ORANLAR = {
  google: 4.5,   // Türkiye dijital hizmet vergisi — Google faturasında tutarın içinde
  meta: 5,       // konum ücreti — Temmuz 2026'dan itibaren
  stopaj: 15,    // brütleştirilir: tutar / (1 − oran) − tutar
  kdv: 20,
  hakedis: 10,   // Glowy hakedişi, HAM harcama üzerinden
  krediliStopaj: 10, // kredili Meta hesapları, KREDILI_BASLANGIC'tan itibaren
};

/** Meta konum ücretinin ilk uygulandığı ay. Düz metin `YYYY-MM` karşılaştırması. */
const META_FEE_BASLANGIC = '2026-07';

/* ── Kredili Meta hesapları (yalnız bu KOPYA sayfada) ───────────────────
   Efe, 2 Eki 2026: HUPALUPA STORE, HUPALUPA Eğlence ve KIXX Meta hesapları
   21 Eyl 2026'da kredili ödemeye geçti. 🔴 21 Eyl ESKİ stopajla hesaplanır,
   düşük stopaj (`krediliStopaj`) 22 Eyl'den itibaren. Keramet kredili DEĞİL.
   Bölme `/api/group/hupalupa/fatura-ayrimi` ucundan gelir (günlük Meta). */
const KREDILI_BASLANGIC = '2026-09-22';
/* Efe, 3 Kas 2026: Keramet Meta da Ekim 2026'dan itibaren kredili stopajla
   (%10) yazılır — yalnız Ekim ve sonrası, Eylül'ü değişmez. */
const KREDILI_SONRADAN = { '2095088354756210': '2026-10' }; // Keramet Ilıcası, ay başından
const krediliMi = (accountId, ay) => {
  const id = String(accountId);
  if (KREDILI_META_HESAPLARI.has(id)) return true;
  return !!(KREDILI_SONRADAN[id] && ay && ay >= KREDILI_SONRADAN[id]);
};
const KREDILI_META_HESAPLARI = new Set([
  '1469447184541059', // HUPALUPA STORE Reklam Hesabı
  '1492081179012995', // HUPALUPA Eğlence Reklam Hesabı
  '4522438977986893', // KIXX
]);

/** `2026-09-22` → "22 Eyl" */
function gunEtiketi(iso) {
  const [, m, d] = String(iso).split('-').map(Number);
  return `${d} ${AY_KISA[m - 1] || ''}`;
}
/** Kesimden bir önceki gün, "21 Eyl" */
function oncekiGunEtiketi(iso) {
  const t = new Date(`${iso}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() - 1);
  return gunEtiketi(t.toISOString().slice(0, 10));
}

/* ── 🔶 GEÇİCİ simülasyon bloğu ────────────────────────────────────────
   Efe, 2 Eyl 2026: "Keramet Ilıcası özelinde bu ay %50 daha az fatura
   keseceğiz" → sayfanın en altında, harcama yarıya inmiş gibi ikinci bir
   fatura merdiveni. 🔴🔴 Yukarıdaki tabloların, özet kartların ve Excel
   çıktısının HİÇBİRİNE dokunmaz; canlı harcama olduğu gibi durur.
   Bu bir SİMÜLASYONDUR — geçici, iş bitince bu blok ve `SIM_*` sabitleri
   silinecek. */
const SIM_MARKA_DESENI = /keramet/i;
const SIM_VARSAYILAN_INDIRIM = 50;

/* ── Biçimleyiciler ────────────────────────────────────────────────── */

const sayi = (n, basamak = 2) =>
  n == null || !Number.isFinite(n)
    ? '—'
    : new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: basamak,
        maximumFractionDigits: basamak,
      }).format(n);

const para = (n, basamak = 2) => (n == null || !Number.isFinite(n) ? '—' : `₺${sayi(n, basamak)}`);

function ayEtiketi(key, kisa = false) {
  const [y, m] = String(key || '').split('-');
  const i = parseInt(m, 10) - 1;
  if (Number.isNaN(i) || !AY_ADLARI[i]) return key || '—';
  return kisa ? `${AY_KISA[i]} ${String(y).slice(2)}` : `${AY_ADLARI[i]} ${y}`;
}

/* ── Hesap ─────────────────────────────────────────────────────────── */

/** O ay o mecraya binen ücret oranı (kesir). Meta'da tarih kuralı işler. */
function ucretOrani(mecra, ay, oranlar) {
  if (mecra.ucret === 'google') return oranlar.google / 100;
  if (mecra.ucret === 'meta') return ay >= META_FEE_BASLANGIC ? oranlar.meta / 100 : 0;
  return 0;
}

/**
 * Tek mecra satırının fatura merdiveni.
 * `ucretYok: true` → o ay o mecrada ücret TANIMSIZ (Meta, Haziran ve öncesi):
 * sütunda "—" yazılır, 0,00 değil.
 */
function satirHesabi(harcama, mecra, ay, oranlar, stopajYuzde = oranlar.stopaj) {
  const oran = ucretOrani(mecra, ay, oranlar);
  const ucretTanimli = mecra.ucret === 'google' || (mecra.ucret === 'meta' && ay >= META_FEE_BASLANGIC);

  const ucret = harcama * oran;
  const ucretli = harcama + ucret;

  // 🔴 Stopaj brütleştirme: net tutarı brüte çıkarır. ×1,15 yazmak kalem
  // başına ~%2,65 eksik verir.
  const s = Math.min(0.99, stopajYuzde / 100);
  const stopaj = s > 0 ? ucretli / (1 - s) - ucretli : 0;
  const stopajli = ucretli + stopaj;

  const kdv = stopajli * (oranlar.kdv / 100);
  const kdvli = stopajli + kdv;

  const hakedis = harcama * (oranlar.hakedis / 100);

  return {
    harcama, ucret, ucretli: ucretTanimli ? ucretli : null, stopaj, stopajli, kdv, kdvli, hakedis, stopajYuzde,
  };
}

/** Markanın bir mecradaki toplam harcaması (Meta iki satıra bölünmüş olabilir). */
const mecraHarcamasi = (marka, key) =>
  marka.satirlar.filter((s) => s.mecra.key === key).reduce((t, s) => t + s.harcama, 0);

/** Faturadan satır süzerek yeni bir fatura: boş kalan marka düşer, toplamlar yeniden. */
function altFatura(fatura, filtre) {
  const markalar = fatura.markalar
    .map((m) => {
      const satirlar = m.satirlar.filter(filtre);
      return { ...m, satirlar, toplam: satirlar.reduce((t, x) => topla(t, x), BOS_TOPLAM()) };
    })
    .filter((m) => m.satirlar.length);
  const grup = markalar.reduce((t, m) => {
    for (const k of Object.keys(t)) t[k] += m.toplam[k];
    return t;
  }, BOS_TOPLAM());
  return { markalar, grup };
}

const BOS_TOPLAM = () => ({
  harcama: 0, ucret: 0, ucretli: 0, stopaj: 0, stopajli: 0, kdv: 0, kdvli: 0, hakedis: 0,
});

function topla(hedef, satir) {
  hedef.harcama += satir.harcama;
  hedef.ucret += satir.ucret;
  hedef.ucretli += satir.ucretli ?? satir.harcama;
  hedef.stopaj += satir.stopaj;
  hedef.stopajli += satir.stopajli;
  hedef.kdv += satir.kdv;
  hedef.kdvli += satir.kdvli;
  hedef.hakedis += satir.hakedis;
  return hedef;
}

/**
 * Bir ayın tüm markaları için fatura tablosu.
 * Marka sırası backend'in verdiği sıradır (grup tanımındaki sıra).
 */
function faturaKur(veri, ay, oranlar, ayrim = null) {
  /* İki dönem (Efe, 2 Eki 2026): Fatura 1 = kesimden önce, Fatura 2 = kesim
     ve sonrası. Google, Meta, TikTok üçü de tarihle bölünür; stopaj her yerde
     normal, YALNIZ kredili Meta hesaplarının 2. dönemi `krediliStopaj`.
     🔑 Kredili OLMAYAN Meta (Keramet) bölünmez, ayın tamamı Fatura 1'de
     ("kerametin metası üstteki tabloda eylülün tamamı gözüksün").
     Ayrım verisi yoksa (yükleniyor/hata) ay tek parça, izgara rakamıyla. */
  const parcalar = ayrim?.parcalar || {};
  const ikiDonem = !!(ayrim && parcalar.once && parcalar.sonra);
  /* Tek dönemli ay: ayın tamamı kesimden ÖNCE (Ağu ve öncesi → 'once') ya da
     tamamı kesimden SONRA (Eki 2026 ve sonrası → 'sonra'). Efe, 3 Kas 2026:
     "Ekimde de Metada stopajı %15 yerine %10 alacağız" → tamamı kesimden
     sonra olan ayda kredili Meta tek tabloda `krediliStopaj` ile yazılır. */
  const tekAnahtar = !ikiDonem && parcalar.sonra && !parcalar.once ? 'sonra' : 'once';
  const donemEtiketi = (d) => {
    const a = parcalar[d === 1 ? 'once' : 'sonra'];
    return a ? `${Number(a.since.slice(8))}–${gunEtiketi(a.until)}` : '';
  };

  /* marka × mecra × dönem harcaması, ayrım ucundan */
  const parca = {}; // `${brandId}|${mecra}` → { once, sonra, kredili: {once, sonra}, devir: {once, sonra} }
  const kab = (b, k) => (parca[`${b}|${k}`] = parca[`${b}|${k}`] || {
    once: 0, sonra: 0, kredili: { once: 0, sonra: 0 }, devir: { once: 0, sonra: 0 },
  });
  if (ayrim) {
    for (const h of ayrim.google || []) {
      const p = kab(h.brandId, 'GOOGLE_ADS'); p.once += h.once; p.sonra += h.sonra;
    }
    for (const h of ayrim.meta || []) {
      const p = kab(h.brandId, 'META_ADS');
      p.once += h.once; p.sonra += h.sonra;
      if (krediliMi(h.accountId, ay)) { p.kredili.once += h.once; p.kredili.sonra += h.sonra; }
    }
    for (const h of ayrim.tiktok || []) {
      for (const k of h.kampanyalar || []) {
        const p = kab(k.brandId, 'TIKTOK_ADS'); p.once += k.once; p.sonra += k.sonra;
        if (k.brandId !== h.bagliMarka) { p.devir.once += k.once; p.devir.sonra += k.sonra; }
      }
    }
  }
  const hataliAyrim = new Set((ayrim?.hatalar || []).map((e) => `${e.brandId}|${e.platform}`));

  const markalar = (veri?.brands || []).map((b) => {
    const m = b.months?.[ay] || {};
    const platformlar = m.platforms || {};
    const hataliMecralar = new Set((b.errors || []).map((e) => e.platform));
    const bagliMecralar = new Set((b.accounts || []).map((a) => a.platform));

    const satirlar = [];
    for (const donem of ikiDonem ? [1, 2] : [1]) {
      const anahtar = ikiDonem ? (donem === 1 ? 'once' : 'sonra') : tekAnahtar;
      for (const mecra of MECRALAR) {
        const ortak = {
          mecra,
          donem,
          donemEtiketi: ikiDonem ? donemEtiketi(donem) : '',
          bagli: bagliMecralar.has(mecra.key),
          hata: hataliMecralar.has(mecra.key),
        };
        const izgara = Number(platformlar[mecra.key]?.spend || 0);
        const p = parca[`${b.id}|${mecra.key}`];

        // Ayrım yok ya da bu hesap ayrımda okunamadı → ayın tamamı 1. dönemde.
        if (!ayrim || hataliAyrim.has(`${b.id}|${mecra.key}`) || (!p && izgara)) {
          if (donem !== 1) continue;
          satirlar.push({
            ...ortak, anahtar: `${mecra.key}-1`,
            ...(ayrim ? { alt: 'ayrılamadı · ayın tamamı', uyari: true } : {}),
            ...satirHesabi(izgara, mecra, ay, oranlar),
          });
          continue;
        }

        if (mecra.key === 'META_ADS') {
          const normalOnce = (p?.once || 0) - (p?.kredili.once || 0);
          const normalSonra = (p?.sonra || 0) - (p?.kredili.sonra || 0);
          const krediliHesapVar = (ayrim.meta || []).some(
            (h) => h.brandId === b.id && krediliMi(h.accountId, ay)
          );
          if (!ikiDonem && tekAnahtar === 'sonra') {
            // Ayın tamamı kredili dönemde: kredili hesaplar `krediliStopaj`,
            // kredili olmayanlar (Keramet) normal stopaj, aynı tabloda.
            if (!krediliHesapVar || normalSonra) {
              satirlar.push({
                ...ortak, anahtar: 'META_ADS-1',
                ...(krediliHesapVar ? { alt: 'kredili değil' } : {}),
                ...satirHesabi(normalSonra, mecra, ay, oranlar),
              });
            }
            if (krediliHesapVar) {
              satirlar.push({
                ...ortak, anahtar: 'META_ADS-1k', kredili: true, alt: 'kredili',
                ...satirHesabi(p?.kredili.sonra || 0, mecra, ay, oranlar, oranlar.krediliStopaj),
              });
            }
            continue;
          }
          if (donem === 1) {
            // Kredili hesabın 1. dönemi + kredili olmayan hesapların AYIN TAMAMI.
            const tumAy = !krediliHesapVar && ikiDonem;
            satirlar.push({
              ...ortak, anahtar: 'META_ADS-1',
              ...(tumAy && normalOnce + normalSonra ? { alt: 'ayın tamamı · kredili değil', donemEtiketi: '' } : {}),
              ...satirHesabi(
                (p?.kredili.once || 0) + normalOnce + (ikiDonem ? normalSonra : 0), mecra, ay, oranlar
              ),
            });
          } else {
            if (!krediliHesapVar) continue;
            satirlar.push({
              ...ortak, anahtar: 'META_ADS-2', kredili: true, alt: 'kredili',
              ...satirHesabi(p?.kredili.sonra || 0, mecra, ay, oranlar, oranlar.krediliStopaj),
            });
          }
          continue;
        }

        satirlar.push({
          ...ortak,
          anahtar: `${mecra.key}-${donem}`,
          devir: mecra.key === 'TIKTOK_ADS' ? (p?.devir[anahtar] || 0) : 0,
          ...satirHesabi(p?.[anahtar] || 0, mecra, ay, oranlar),
        });
      }
    }

    return {
      id: b.id,
      label: b.label,
      logo: b.logo,
      accent: b.accent,
      satirlar,
      toplam: satirlar.reduce((t, s) => topla(t, s), BOS_TOPLAM()),
    };
  });

  const grup = markalar.reduce(
    (t, m) => {
      t.harcama += m.toplam.harcama;
      t.ucret += m.toplam.ucret;
      t.ucretli += m.toplam.ucretli;
      t.stopaj += m.toplam.stopaj;
      t.stopajli += m.toplam.stopajli;
      t.kdv += m.toplam.kdv;
      t.kdvli += m.toplam.kdvli;
      t.hakedis += m.toplam.hakedis;
      return t;
    },
    BOS_TOPLAM()
  );

  return { markalar, grup };
}

/* ── Küçük parçalar ────────────────────────────────────────────────── */

/** Sağa yaslı para hücresi. `bos` → ölçülmüyor/tanımsız, 0 DEĞİL. */
function P({ v, guclu = false, soluk = false, sinif = '' }) {
  const yok = v == null || !Number.isFinite(v);
  return (
    <td
      className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${
        guclu ? 'font-semibold text-slate-900' : soluk || yok ? 'text-slate-400' : 'text-slate-700'
      } ${sinif}`}
    >
      {yok ? '—' : sayi(v)}
    </td>
  );
}

function OranKutusu({ etiket, deger, onChange, ipucu }) {
  return (
    <label className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-slate-200" title={ipucu}>
      <span className="text-[11px] font-medium text-slate-500">{etiket}</span>
      <span className="text-[11px] text-slate-400">%</span>
      <input
        type="number"
        step="0.05"
        min="0"
        max="99"
        value={deger}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-14 rounded border-0 bg-slate-50 px-1.5 py-0.5 text-right text-xs font-semibold tabular-nums text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400"
      />
    </label>
  );
}

/**
 * Kopya sayfanın iki düzeltmesini gösterir — rakamlar nereden geldi
 * sorusunun cevabı ekranda dursun.
 */
function AyrimPaneli({ durum, veri, oranlar, seciliAy }) {
  if (!durum) return null;
  if (durum.yukleniyor) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Kredili Meta ve TikTok kampanya ayrımı çekiliyor. Tablo şimdilik ayrımsız.
      </div>
    );
  }
  if (durum.hata) {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Ayrım çekilemedi, tablo <b>ayrımsız</b> (asıl sayfayla aynı): {durum.hata}</span>
      </div>
    );
  }
  const marka = (id) => (veri?.brands || []).find((b) => b.id === id)?.label || id || '—';
  const krediliAy = seciliAy >= KREDILI_BASLANGIC.slice(0, 7);
  const krediliHesaplar = (durum.meta || []).filter((h) => krediliMi(h.accountId, seciliAy));
  const paylasilan = (durum.tiktok || []).filter((h) => h.paylasilan && (h.kampanyalar || []).length);

  return (
    <div className="mt-3 grid gap-3 lg:grid-cols-2">
      <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Meta · kredili hesaplar</div>
        {!krediliAy ? (
          <p className="mt-1.5 text-xs text-slate-500">
            Bu ayda kredili hesap yok. Kredili stopaj {gunEtiketi(KREDILI_BASLANGIC)} {KREDILI_BASLANGIC.slice(0, 4)}'den itibaren uygulanır.
          </p>
        ) : (
          <table className="mt-1.5 w-full text-xs">
            <thead>
              <tr className="text-slate-400">
                <th className="py-1 text-left font-medium">Hesap</th>
                <th className="py-1 text-right font-medium">
                  {oncekiGunEtiketi(KREDILI_BASLANGIC)}'e kadar · %{sayi(oranlar.stopaj, 0)}
                </th>
                <th className="py-1 text-right font-medium">
                  {gunEtiketi(KREDILI_BASLANGIC)}'den · %{sayi(oranlar.krediliStopaj, 0)}
                </th>
              </tr>
            </thead>
            <tbody>
              {krediliHesaplar.map((h) => (
                <tr key={h.accountId} className="border-t border-slate-100">
                  <td className="py-1 text-slate-700">{h.name}</td>
                  <td className="py-1 text-right tabular-nums text-slate-600">{sayi(h.once)}</td>
                  <td className="py-1 text-right font-semibold tabular-nums text-sky-700">{sayi(h.sonra)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          TikTok · paylaşılan hesap kampanya ayrımı
        </div>
        {!paylasilan.length ? (
          <p className="mt-1.5 text-xs text-slate-500">Bu ayda paylaşılan hesapta harcama yok.</p>
        ) : (
          paylasilan.map((h) => (
            <div key={h.advertiserId} className="mt-1.5">
              <div className="text-[11px] text-slate-400">
                {h.name} ({h.advertiserId}) · asıl sayfada tamamı {marka(h.bagliMarka)}'de · adında “store” veya “needoh” geçen → HUPALUPA Store
              </div>
              <table className="mt-1 w-full text-xs">
                <tbody>
                  {h.kampanyalar.map((k) => (
                    <tr key={k.id} className="border-t border-slate-100">
                      <td className="py-1 text-slate-700">{k.name}</td>
                      <td className="py-1 text-slate-500">{marka(k.brandId || h.bagliMarka)}</td>
                      <td className="py-1 text-right tabular-nums text-slate-700">{sayi(k.spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ── Excel ─────────────────────────────────────────────────────────── */

const XL_PARA = '#,##0.00';
const xlKenar = {
  top: { style: 'thin', color: { rgb: 'CBD5E1' } },
  bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
  left: { style: 'thin', color: { rgb: 'CBD5E1' } },
  right: { style: 'thin', color: { rgb: 'CBD5E1' } },
};
const XS = {
  baslik: {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: xlKenar,
  },
  metin: { font: { sz: 10 }, alignment: { horizontal: 'left', vertical: 'center' }, border: xlKenar },
  marka: {
    font: { bold: true, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: xlKenar,
  },
  sayi: { font: { sz: 10 }, alignment: { horizontal: 'right', vertical: 'center' }, border: xlKenar },
  vurgu: {
    font: { bold: true, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: 'FEF9C3' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  toplam: {
    font: { bold: true, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  grup: {
    font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  not: { font: { sz: 9, color: { rgb: '475569' } }, alignment: { horizontal: 'left', vertical: 'top', wrapText: true } },
};
const xt = (v, s = XS.metin) => ({ v: v == null ? '' : String(v), t: 's', s });
const xn = (v, s = XS.sayi) =>
  v == null || !Number.isFinite(v) ? { v: '—', t: 's', s } : { v: Number(v), t: 'n', z: XL_PARA, s };

/* ── Sayfa ─────────────────────────────────────────────────────────── */

/**
 * Tek fatura tablosu. Sayfada İKİ kez çizilir (Efe, 2 Eki 2026: "21'ine kadar
 * olanı başka tabloda, 22'sinden olanı başka tabloda göster, fatura keseceğim"):
 *   1) Google + TikTok (ayın tamamı) + Meta kredili öncesi → normal stopaj
 *   2) Yalnız kredili Meta (KREDILI_BASLANGIC'tan sonrası) → kredili stopaj
 */
function FaturaTablosu({ fatura, oranlar, baslik, aciklama, stopajBaslik = null, vurgu = false }) {
  const g = fatura.grup;
  return (
    <div className="mt-4">
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2 px-1">
        <div>
          <span className={`text-sm font-bold ${vurgu ? 'text-sky-800' : 'text-slate-900'}`}>{baslik}</span>
          {aciklama && <span className="ml-2 text-[11px] text-slate-500">{aciklama}</span>}
        </div>
        <div className="text-xs tabular-nums text-slate-600">
          KDV hariç <b className="text-slate-900">{para(g.stopajli)}</b>
          <span className="mx-1.5 text-slate-300">·</span>
          KDV dahil <b className="text-slate-900">{para(g.kdvli)}</b>
        </div>
      </div>
    <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
      <table className="w-full min-w-[1280px] border-collapse text-xs">
        <thead>
          <tr className="bg-slate-900 text-white">
            <th className="px-3 py-2 text-left font-semibold">Marka</th>
            <th className="px-3 py-2 text-left font-semibold">Mecra</th>
            <th className="px-3 py-2 text-right font-semibold">Harcama</th>
            <th className="px-3 py-2 text-right font-semibold">
              Mecra Ücreti Dahil
              <div className="text-[10px] font-normal text-slate-300">
                Google %{sayi(oranlar.google, 1)} · Meta %{sayi(oranlar.meta, 1)}
              </div>
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {stopajBaslik ? 'Stopaj' : `Stopaj %${sayi(oranlar.stopaj, 0)}`}
              {stopajBaslik && <div className="text-[10px] font-normal text-slate-300">{stopajBaslik}</div>}
            </th>
            <th className="px-3 py-2 text-right font-semibold">Stopaj Dahil</th>
            <th className="px-3 py-2 text-right font-semibold">KDV %{sayi(oranlar.kdv, 0)}</th>
            <th className="px-3 py-2 text-right font-semibold">KDV Dahil</th>
            <th className="border-l border-slate-700 px-3 py-2 text-right font-semibold">
              Marka Toplamı
              <div className="text-[10px] font-normal text-slate-300">KDV hariç</div>
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              Marka Toplamı
              <div className="text-[10px] font-normal text-slate-300">KDV dahil</div>
            </th>
            <th className="border-l border-slate-700 px-3 py-2 text-right font-semibold">
              Glowy Hakediş
              <div className="text-[10px] font-normal text-slate-300">harcamanın %{sayi(oranlar.hakedis, 0)}'u</div>
            </th>
            <th className="px-3 py-2 text-right font-semibold">Hakediş Toplamı</th>
          </tr>
        </thead>

        {fatura.markalar.map((m) => (
          <tbody key={m.id} className="border-t-2 border-slate-200">
            {m.satirlar.map((s, i) => (
              <tr key={s.anahtar} className={`border-t border-slate-100 hover:bg-slate-50/60 ${s.kredili ? 'bg-sky-50/50' : ''}`}>
                {i === 0 && (
                  <td
                    rowSpan={m.satirlar.length}
                    className="border-r border-slate-100 bg-slate-50/70 px-3 py-2 align-middle"
                  >
                    <div className="flex items-center gap-2">
                      {m.logo && (
                        <img
                          src={m.logo}
                          alt=""
                          className="h-6 w-6 shrink-0 rounded object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <span className="font-semibold text-slate-900">{m.label}</span>
                    </div>
                  </td>
                )}

                <td className="whitespace-nowrap px-3 py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <img
                      src={s.mecra.logo}
                      alt=""
                      className="h-3.5 w-3.5 object-contain"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <span className={s.bagli ? 'text-slate-700' : 'text-slate-400'}>{s.mecra.label}</span>
                    {s.alt && (
                      <span className={`text-[10px] ${s.kredili ? 'font-semibold text-sky-700' : s.uyari ? 'font-semibold text-rose-700' : 'text-slate-400'}`}>
                        {s.alt} · stopaj %{sayi(s.stopajYuzde, 0)}
                      </span>
                    )}
                    {s.devir ? (
                      <span
                        title="Paylaşılan TikTok hesabından kampanya adına göre aktarılan harcama (store/needoh → Store)"
                        className={`rounded px-1 text-[10px] font-semibold ${s.devir > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                      >
                        +{sayi(s.devir, 0)} Eğlence hesabından
                      </span>
                    ) : null}
                    {s.hata && (
                      <span
                        title="Bu mecrada okunamayan hesap var — harcama eksik olabilir"
                        className="rounded bg-rose-100 px-1 text-[10px] font-semibold text-rose-700"
                      >
                        !
                      </span>
                    )}
                    {!s.bagli && (
                      <span
                        title="Bu markada bu mecranın bağlı hesabı yok"
                        className="text-[10px] text-slate-400"
                      >
                        bağlı değil
                      </span>
                    )}
                  </span>
                </td>

                {/* Harcama — Excel'de sarı boyanan sütun, faturanın matrahı */}
                <P v={s.harcama} guclu sinif="bg-amber-50/70" />
                <P v={s.ucretli} />
                <P v={s.stopaj} />
                <P v={s.stopajli} />
                <P v={s.kdv} />
                <P v={s.kdvli} guclu />

                {/* 🔑 İki marka toplamı (Efe, 2 Eyl 2026): KDV hariç =
                    kesilecek faturanın matrahı, KDV dahil = ödenecek tutar. */}
                {i === 0 && (
                  <td
                    rowSpan={m.satirlar.length}
                    className="whitespace-nowrap border-l border-slate-200 bg-slate-50 px-3 py-2 text-right align-middle text-sm font-semibold tabular-nums text-slate-600"
                  >
                    {sayi(m.toplam.stopajli)}
                  </td>
                )}

                {i === 0 && (
                  <td
                    rowSpan={m.satirlar.length}
                    className="whitespace-nowrap bg-slate-50 px-3 py-2 text-right align-middle text-sm font-bold tabular-nums text-slate-900"
                  >
                    {sayi(m.toplam.kdvli)}
                  </td>
                )}

                <P v={s.hakedis} sinif="border-l border-slate-200 bg-amber-50/70 font-medium" />

                {i === 0 && (
                  <td
                    rowSpan={m.satirlar.length}
                    className="whitespace-nowrap bg-slate-50 px-3 py-2 text-right align-middle text-sm font-bold tabular-nums text-slate-900"
                  >
                    {sayi(m.toplam.hakedis)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        ))}

        <tfoot>
          <tr className="bg-slate-900 text-white">
            <td className="px-3 py-2.5 font-bold uppercase tracking-wide" colSpan={2}>
              Grup Toplamı
            </td>
            <td className="px-3 py-2.5 text-right font-bold tabular-nums">{sayi(g.harcama)}</td>
            <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.ucretli)}</td>
            <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.stopaj)}</td>
            <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.stopajli)}</td>
            <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.kdv)}</td>
            <td className="px-3 py-2.5 text-right font-bold tabular-nums">{sayi(g.kdvli)}</td>
            <td className="border-l border-slate-700 px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-slate-200">
              {sayi(g.stopajli)}
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums">
              {sayi(g.kdvli)}
            </td>
            <td className="border-l border-slate-700 px-3 py-2.5 text-right font-bold tabular-nums">
              {sayi(g.hakedis)}
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums">{sayi(g.hakedis)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    </div>
  );
}

/**
 * Kesilecek faturaların marka marka özeti (Efe, 2 Eki 2026: "eylül için
 * kesmem gereken fatura toplam kaç para, altta marka toplamını görmem lazım").
 * Rakamlar iki fatura tablosunun marka toplamlarıdır, ayrıca hesap yok.
 */
function FaturaOzeti({ fatura1, fatura2, baslik1, baslik2, ay }) {
  const ids = [...new Set([...fatura1.markalar, ...fatura2.markalar].map((m) => m.id))];
  const bul = (f, id) => f.markalar.find((m) => m.id === id);
  const satirlar = ids.map((id) => {
    const a = bul(fatura1, id);
    const b = bul(fatura2, id);
    const m = a || b;
    return {
      id, label: m.label, logo: m.logo,
      h1: a?.toplam.stopajli || 0, d1: a?.toplam.kdvli || 0,
      h2: b?.toplam.stopajli || 0, d2: b?.toplam.kdvli || 0,
    };
  });
  const T = satirlar.reduce(
    (t, r) => ({ h1: t.h1 + r.h1, d1: t.d1 + r.d1, h2: t.h2 + r.h2, d2: t.d2 + r.d2 }),
    { h1: 0, d1: 0, h2: 0, d2: 0 }
  );
  const H = 'whitespace-nowrap px-3 py-2 text-right tabular-nums';
  return (
    <div className="mt-5 overflow-hidden rounded-xl bg-white ring-2 ring-slate-900">
      <div className="flex flex-wrap items-baseline justify-between gap-2 bg-slate-900 px-4 py-3 text-white">
        <span className="text-sm font-bold">{ayEtiketi(ay)} · kesilecek faturalar</span>
        <span className="text-sm tabular-nums">
          Toplam KDV dahil <b className="text-lg">{para(T.d1 + T.d2)}</b>
          <span className="ml-2 text-xs text-slate-300">KDV hariç {para(T.h1 + T.h2)}</span>
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-600">
              <th rowSpan={2} className="px-3 py-2 text-left font-semibold">Marka</th>
              <th colSpan={2} className="border-l border-slate-200 px-3 pt-2 text-center font-semibold">{baslik1}</th>
              <th colSpan={2} className="border-l border-slate-200 px-3 pt-2 text-center font-semibold text-sky-800">{baslik2}</th>
              <th colSpan={2} className="border-l border-slate-200 px-3 pt-2 text-center font-semibold">{ayEtiketi(ay)} toplam</th>
            </tr>
            <tr className="bg-slate-50 text-[10px] text-slate-400">
              {[0, 1, 2].map((i) => (
                <React.Fragment key={i}>
                  <th className="border-l border-slate-200 px-3 pb-2 text-right font-medium">KDV hariç</th>
                  <th className="px-3 pb-2 text-right font-medium">KDV dahil</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {satirlar.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="whitespace-nowrap px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    {r.logo && (
                      <img src={r.logo} alt="" className="h-5 w-5 shrink-0 rounded object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <span className="font-semibold text-slate-900">{r.label}</span>
                  </span>
                </td>
                <td className={`${H} border-l border-slate-200 text-slate-500`}>{sayi(r.h1)}</td>
                <td className={`${H} font-semibold text-slate-900`}>{sayi(r.d1)}</td>
                <td className={`${H} border-l border-slate-200 text-slate-500`}>{r.h2 ? sayi(r.h2) : '—'}</td>
                <td className={`${H} font-semibold text-sky-800`}>{r.d2 ? sayi(r.d2) : '—'}</td>
                <td className={`${H} border-l border-slate-200 bg-amber-50/60 text-slate-600`}>{sayi(r.h1 + r.h2)}</td>
                <td className={`${H} bg-amber-50/60 text-sm font-bold text-slate-900`}>{sayi(r.d1 + r.d2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-900 text-white">
              <td className="px-3 py-2.5 font-bold uppercase tracking-wide">Toplam</td>
              <td className={`${H} border-l border-slate-700 text-slate-300`}>{sayi(T.h1)}</td>
              <td className={`${H} font-bold`}>{sayi(T.d1)}</td>
              <td className={`${H} border-l border-slate-700 text-slate-300`}>{sayi(T.h2)}</td>
              <td className={`${H} font-bold`}>{sayi(T.d2)}</td>
              <td className={`${H} border-l border-slate-700 text-slate-300`}>{sayi(T.h1 + T.h2)}</td>
              <td className={`${H} text-sm font-bold`}>{sayi(T.d1 + T.d2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function HupalupaGroupBillingKopya() {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [seciliAy, setSeciliAy] = useState(null);
  const [oranlar, setOranlar] = useState(VARSAYILAN_ORANLAR);
  /* ay → `/fatura-ayrimi` cevabı | { yukleniyor } | { hata } */
  const [ayrimlar, setAyrimlar] = useState({});

  const getir = useCallback(async (yenile = false) => {
    setYukleniyor(true);
    setHata(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/group/hupalupa/monthly?period=ay&count=${AY_SAYISI}${yenile ? '&refresh=1' : ''}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include',
        }
      );
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      if (yenile) setAyrimlar({});
      setVeri(json);
      setSeciliAy((onceki) =>
        onceki && json.months.includes(onceki) ? onceki : json.months[json.months.length - 1]
      );
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { getir(false); }, [getir]);

  /* Seçili ayın kredili Meta + TikTok kampanya ayrımı — ay başına bir kez. */
  useEffect(() => {
    if (!seciliAy || ayrimlar[seciliAy]) return;
    const ay = seciliAy;
    setAyrimlar((a) => ({ ...a, [ay]: { yukleniyor: true } }));
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/group/hupalupa/fatura-ayrimi?ay=${ay}&kesim=${KREDILI_BASLANGIC}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            credentials: 'include',
          }
        );
        const json = await res.json();
        if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
        setAyrimlar((a) => ({ ...a, [ay]: json }));
      } catch (e) {
        setAyrimlar((a) => ({ ...a, [ay]: { hata: e.message } }));
      }
    })();
  }, [seciliAy, ayrimlar]);

  const ayrim = seciliAy && ayrimlar[seciliAy]?.ok ? ayrimlar[seciliAy] : null;

  const fatura = useMemo(
    () => (veri && seciliAy ? faturaKur(veri, seciliAy, oranlar, ayrim) : null),
    [veri, seciliAy, oranlar, ayrim]
  );

  /* Alt tablo: her ayın grup toplamı — fatura tutarı ay ay nasıl gitmiş. */
  const aylikOzet = useMemo(() => {
    if (!veri) return [];
    return veri.months.map((ay) => ({
      ay,
      ...faturaKur(veri, ay, oranlar, ayrimlar[ay]?.ok ? ayrimlar[ay] : null).grup,
    }));
  }, [veri, oranlar, ayrimlar]);

  /* Kısmi ay uyarısı: içinde bulunulan ay henüz kapanmadı, fatura tutarı
     büyümeye devam eder. Patron panelinde bu tuzak bir kez ısırdı. */
  const donemBilgisi = useMemo(
    () => (veri?.periods || []).find((d) => d.key === seciliAy) || null,
    [veri, seciliAy]
  );
  // 🔑 Backend zaten söylüyor (`devamEdiyor`); burada tarihten yeniden
  // türetmek saat dilimi farkında bir gün kayıyor.
  const kismiAy = !!donemBilgisi?.devamEdiyor;

  /* 🔶 GEÇİCİ: Keramet indirim simülasyonu (yukarıdaki hesaba karışmaz). */
  const [simIndirim, setSimIndirim] = useState(SIM_VARSAYILAN_INDIRIM);
  const simulasyon = useMemo(() => {
    const marka = (fatura?.markalar || []).find((m) => SIM_MARKA_DESENI.test(m.label || ''));
    if (!marka) return null;
    const carpan = Math.max(0, 1 - simIndirim / 100);
    const satirlar = marka.satirlar.map((s) => ({
      mecra: s.mecra,
      mevcut: s.harcama,
      anahtar: s.anahtar,
      alt: s.alt,
      ...satirHesabi(s.harcama * carpan, s.mecra, seciliAy, oranlar, s.stopajYuzde),
    }));
    return {
      marka,
      carpan,
      satirlar,
      toplam: satirlar.reduce((t, s) => topla(t, s), BOS_TOPLAM()),
    };
  }, [fatura, simIndirim, seciliAy, oranlar]);

  const tumHatalar = useMemo(
    () => (veri?.brands || []).flatMap((b) => (b.errors || []).map((e) => ({ marka: b.label, ...e }))),
    [veri]
  );
  /* 🔴 TL dışı hesap fatura tablosunu sessizce bozar — çevrim YAPILMIYOR. */
  const yabanciParaBirimi = useMemo(
    () =>
      (veri?.brands || [])
        .flatMap((b) => (b.accounts || []).map((a) => ({ marka: b.label, ...a })))
        .filter((a) => a.currency && !['TRY', 'TL'].includes(String(a.currency).toUpperCase())),
    [veri]
  );

  /**
   * Excel çıktısı — hücreler SABİT DEĞİL, canlı FORMÜL.
   *
   * 🔑🔑 Oranlar sayfanın başında kendi hücrelerinde durur (`$B$4`…`$B$8`) ve
   * bütün merdiven onlara bağlıdır: muhasebe stopajı %15'ten %20'ye çekince
   * tablo kendi kendine yeniden hesaplar. Tutarı elle yazsaydık dosya
   * indirildiği andan itibaren ölü bir fotoğraf olurdu.
   *
   * 🔑 Her hücre hem `f` (formül) hem `v` (hesaplanmış değer) taşır: formülü
   * çalıştırmayan görüntüleyiciler (önizleme, Google Drive'ın hızlı bakışı)
   * yine de doğru rakamı gösterir.
   *
   * 🔴 Ücret sütunu ücretsiz mecralarda (TikTok, Temmuz 2026 öncesi Meta) boş
   * BIRAKILMAZ, `=C{n}` yazılır — ekranda "—" görünen yerde Excel'de zincirin
   * kopmaması için. Boş bıraksaydık altındaki stopaj formülü sıfıra düşerdi.
   */
  const excelIndir = useCallback(() => {
    if (!fatura || !seciliAy) return;

    /* Formül + hesaplanmış değer taşıyan hücre. */
    const xf = (formul, deger, stil = XS.sayi) => ({
      t: 'n', f: formul, v: Number.isFinite(deger) ? deger : 0, z: XL_PARA, s: stil,
    });
    const yuzde = (v, stil = XS.sayi) => ({ t: 'n', v, z: '0.00%', s: stil });

    const sayfaAdi = ayEtiketi(seciliAy, true);
    /* Sayfa adında boşluk var → başka sayfadan atıfta tırnak ŞART. */
    const ref = (adres) => `'${sayfaAdi}'!${adres}`;

    /* ── Oran bloğu (satır 4-8; A sütunu etiket, B sütunu değer) ── */
    const O = { google: 'B4', meta: 'B5', stopaj: 'B6', kdv: 'B7', hakedis: 'B8', kredili: 'B10' };
    const M = (a) => `$${a[0]}$${a.slice(1)}`; // B4 → $B$4

    /* 🔑 Marka toplamı İKİ sütun: I = KDV hariç (stopaj dahil matrah, kesilecek
       faturanın tutarı), J = KDV dahil. Sütun harfleri sayfa 2 ve 3'ten atıfla
       kullanılıyor — sıra değişirse orayı da güncelle. */
    const B = [
      'Marka', 'Mecra', 'Harcama', 'Mecra Ücreti Dahil',
      'Stopaj', 'Stopaj Dahil', 'KDV', 'KDV Dahil',
      'Marka Toplamı (KDV Hariç)', 'Marka Toplamı (KDV Dahil)',
      'Glowy Hakediş', 'Hakediş Toplamı',
    ];

    const satirlar = [
      [xt(`HUPALUPA Grubu · Faturalandırma · ${ayEtiketi(seciliAy)}`, XS.marka)],
      [xt('Aşağıdaki oranları değiştirin, tablo kendini yeniden hesaplar.', XS.not)],
      [],
      [xt('Google ücreti', XS.marka), yuzde(oranlar.google / 100, XS.vurgu)],
      [xt('Meta ücreti', XS.marka), yuzde(oranlar.meta / 100, XS.vurgu)],
      [xt('Stopaj', XS.marka), yuzde(oranlar.stopaj / 100, XS.vurgu)],
      [xt('KDV', XS.marka), yuzde(oranlar.kdv / 100, XS.vurgu)],
      [xt('Glowy hakedişi', XS.marka), yuzde(oranlar.hakedis / 100, XS.vurgu)],
      [xt('Meta ücreti başlangıcı', XS.marka), xt(META_FEE_BASLANGIC, XS.metin)],
      [xt(`Kredili stopaj (${KREDILI_BASLANGIC}'den)`, XS.marka), yuzde(oranlar.krediliStopaj / 100, XS.vurgu)],
    ];

    /* 🔑 Eylül 2026'dan itibaren ay İKİ faturaya bölünür (ekrandaki gibi):
       Fatura 1 = kesimden önce, Fatura 2 = kesim ve sonrası. Aynı sayfada alt
       alta iki blok; ikisi de yukarıdaki oran hücrelerine bağlı. Bölünmeyen
       ayda tek blok. */
    const ikiDonemX = fatura.markalar.some((m) => m.satirlar.some((x) => x.donem === 2));
    const donemYazisiX = (d) =>
      fatura.markalar.flatMap((m) => m.satirlar).find((x) => x.donem === d && x.donemEtiketi)?.donemEtiketi || '';
    const bloklar = ikiDonemX
      ? [
          { no: 1, baslik: `FATURA 1 · ${donemYazisiX(1)}`, f: altFatura(fatura, (x) => x.donem !== 2) },
          { no: 2, baslik: `FATURA 2 · ${donemYazisiX(2)}`, f: altFatura(fatura, (x) => x.donem === 2) },
        ]
      : [{ no: 1, baslik: null, f: fatura }];

    const merges = [];
    for (const blok of bloklar) {
      satirlar.push([]);
      if (blok.baslik) satirlar.push([xt(blok.baslik, XS.marka)]);
      satirlar.push(B.map((h) => xt(h, XS.baslik)));
      const ilkVeriSatiri = satirlar.length; // 0 tabanlı
      /* marka id → Excel satırları; özet sayfası buradan atıf yapar. */
      blok.markaSatirlari = {};
      for (const m of blok.f.markalar) {
        const ilk = satirlar.length;
        const kayit = { ilk: ilk + 1, satirlar: [] };
        blok.markaSatirlari[m.id] = kayit;
        m.satirlar.forEach((sat, i) => {
          const r = satirlar.length + 1; // Excel 1 tabanlı
          kayit.satirlar.push({ key: sat.mecra.key, r });
          /* Ücret oranı hücresi: Google her ay, Meta yalnız başlangıçtan sonra.
             Ücretsiz satırda formül `=C{r}` — zincir kopmasın. */
          const oranHucre =
            sat.mecra.ucret === 'google'
              ? M(O.google)
              : sat.mecra.ucret === 'meta' && seciliAy >= META_FEE_BASLANGIC
              ? M(O.meta)
              : null;

          satirlar.push([
            i === 0 ? xt(m.label, XS.marka) : xt('', XS.marka),
            xt([sat.mecra.label, sat.alt].filter(Boolean).join(' · ')),
            // Harcama TEK ham veri — merdivenin tamamı buna bağlı.
            { t: 'n', v: sat.harcama, z: XL_PARA, s: XS.vurgu },
            xf(oranHucre ? `C${r}*(1+${oranHucre})` : `C${r}`, sat.ucretli ?? sat.harcama),
            xf(`D${r}/(1-${M(sat.kredili ? O.kredili : O.stopaj)})-D${r}`, sat.stopaj),
            xf(`D${r}+E${r}`, sat.stopajli),
            xf(`F${r}*${M(O.kdv)}`, sat.kdv),
            xf(`F${r}+G${r}`, sat.kdvli, XS.toplam),
            null, // marka toplamı (KDV hariç) — birleştirilmiş hücreye yazılır
            null, // marka toplamı (KDV dahil) — birleştirilmiş hücreye yazılır
            xf(`C${r}*${M(O.hakedis)}`, sat.hakedis, XS.vurgu),
            null,
          ]);
        });
        const son = satirlar.length - 1;
        const ilkE = ilk + 1;
        const sonE = son + 1;
        satirlar[ilk][8] = xf(`SUM(F${ilkE}:F${sonE})`, m.toplam.stopajli, XS.toplam);
        satirlar[ilk][9] = xf(`SUM(H${ilkE}:H${sonE})`, m.toplam.kdvli, XS.toplam);
        satirlar[ilk][11] = xf(`SUM(K${ilkE}:K${sonE})`, m.toplam.hakedis, XS.toplam);
        for (let r = ilk + 1; r <= son; r++) {
          satirlar[r][8] = xt('', XS.toplam);
          satirlar[r][9] = xt('', XS.toplam);
          satirlar[r][11] = xt('', XS.toplam);
        }
        merges.push({ s: { r: ilk, c: 0 }, e: { r: son, c: 0 } });
        merges.push({ s: { r: ilk, c: 8 }, e: { r: son, c: 8 } });
        merges.push({ s: { r: ilk, c: 9 }, e: { r: son, c: 9 } });
        merges.push({ s: { r: ilk, c: 11 }, e: { r: son, c: 11 } });
      }

      const gb = blok.f.grup;
      const vBas = ilkVeriSatiri + 1;                 // ilk veri satırı, Excel 1 tabanlı
      const vSon = satirlar.length;                   // son veri satırı
      /* 🔑 I, J ve L birleştirilmiş sütunlar: yalnız marka başlarında sayı var,
         aradaki hücreler metin — `SUM` metni yok sayar, çift toplama olmaz. */
      satirlar.push([
        xt(blok.baslik ? `${blok.baslik.split(' · ')[0]} TOPLAMI` : 'GRUP TOPLAMI', XS.grup), xt('', XS.grup),
        ...['C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].map((sut, i) =>
          xf(`SUM(${sut}${vBas}:${sut}${vSon})`,
            [gb.harcama, gb.ucretli, gb.stopaj, gb.stopajli, gb.kdv, gb.kdvli,
             gb.stopajli, gb.kdvli, gb.hakedis, gb.hakedis][i],
            XS.grup)
        ),
      ]);
      blok.grupSatiri = satirlar.length; // 1 tabanlı: blok toplam satırı
    }
    const g = fatura.grup;
    /* Ay toplamı = blokların toplam satırları (sütun harfiyle atıf). */
    const ayTop = (sut) => bloklar.map((bk) => ref(`${sut}${bk.grupSatiri}`)).join('+');

    /* 🔴 Sayfanın altındaki AÇIKLAMA BLOĞU bilerek kaldırıldı (Efe, 1 Eyl 2026:
       "şu satırları kaldır") — merdiven, brütleştirme, hakediş matrahı ve Meta
       tarih kuralını anlatan maddeler. Geri koyma; formüller zaten hücrelerde
       görünüyor. Kalan tek satır kısmi ay UYARISI: o bir açıklama değil,
       "bu rakam henüz kesin değil" bayrağı ve yalnız ay kapanmamışsa yazılır. */
    if (kismiAy) {
      satirlar.push([]);
      satirlar.push([xt('DİKKAT: seçili ay henüz kapanmadı, tutarlar ay sonuna kadar artacak.', XS.not)]);
    }

    const ws = {};
    let maxC = B.length - 1;
    satirlar.forEach((satir, r) => {
      (satir || []).forEach((h, c) => {
        if (h == null) return;
        ws[XLSX.utils.encode_cell({ r, c })] = h;
        if (c > maxC) maxC = c;
      });
    });
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: satirlar.length - 1, c: maxC } });
    ws['!cols'] = [{ wch: 22 }, { wch: 13 }, ...Array.from({ length: 10 }, () => ({ wch: 16 }))];
    ws['!merges'] = merges;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sayfaAdi);

    /* ── İkinci sayfa: ay ay grup toplamı ──────────────────────────────
       🔴 Burada formül YOK ve olamaz: diğer ayların mecra kırılımı bu dosyada
       hiç yok, atıf yapacak hücre bulunmuyor. Rakamlar panelde hesaplanmış
       hâlleriyle yazılır; seçili ayın satırı sayfa 1'e bağlanır. */
    const o = [
      [xt('Ay', XS.baslik), xt('Harcama', XS.baslik), xt('Mecra ücreti', XS.baslik),
       xt('Fatura Toplamı', XS.baslik), xt('Glowy Hakediş', XS.baslik)],
    ];
    aylikOzet.forEach((r) => {
      const bu = r.ay === seciliAy;
      o.push([
        xt(ayEtiketi(r.ay), bu ? XS.marka : XS.metin),
        bu ? xf(ayTop('C'), r.harcama) : xn(r.harcama),
        bu ? xf(`(${ayTop('D')})-(${ayTop('C')})`, r.ucret) : xn(r.ucret),
        bu ? xf(ayTop('H'), r.kdvli) : xn(r.kdvli),
        bu ? xf(ayTop('K'), r.hakedis) : xn(r.hakedis),
      ]);
    });
    const ws2 = {};
    o.forEach((satir, r) => satir.forEach((h, c) => { ws2[XLSX.utils.encode_cell({ r, c })] = h; }));
    ws2['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: o.length - 1, c: 4 } });
    ws2['!cols'] = Array.from({ length: 5 }, () => ({ wch: 16 }));
    XLSX.utils.book_append_sheet(wb, ws2, 'Aylık özet');

    /* ── Üçüncü sayfa: kesilecek faturalar, marka marka (SEÇİLİ AY) ──────
       Efe, 2 Eki 2026: "her markaya tek medya faturası, tek hizmet faturası".
       Medya = fatura bloklarının marka toplamı (KDV hariç/dahil); hizmet =
       Glowy hakedişi (+KDV, stopaj YOK). Tamamı sayfa 1'e atıf. */
    const mb = [
      'Marka',
      ...bloklar.flatMap((bk) => {
        const ad = bk.baslik ? bk.baslik.replace('FATURA', 'Fatura') : 'Medya';
        return [`${ad} (KDV hariç)`, `${ad} (KDV dahil)`];
      }),
      'MEDYA FATURASI (KDV hariç)', 'MEDYA FATURASI (KDV dahil)',
      'HİZMET FATURASI (KDV hariç)', 'HİZMET FATURASI (KDV dahil)',
    ];
    const mo = [
      [xt(`Kesilecek faturalar · ${ayEtiketi(seciliAy)}`, XS.marka)],
      [xt(`Bütün hücreler '${sayfaAdi}' sayfasına bağlıdır. Hizmet faturası = Glowy hakedişi + KDV.`, XS.not)],
      [],
      mb.map((h) => xt(h, XS.baslik)),
    ];
    const harf = (c) => XLSX.utils.encode_col(c);
    const nb = bloklar.length;
    fatura.markalar.forEach((m) => {
      const r = mo.length + 1;                       // bu satırın Excel numarası
      const hucreler = [xt(m.label, XS.marka)];
      const haricS = [];
      const dahilS = [];
      const hakedisRef = [];
      bloklar.forEach((bk, bi) => {
        const k = bk.markaSatirlari[m.id];
        const bm = bk.f.markalar.find((x) => x.id === m.id);
        const c = 1 + bi * 2;
        hucreler.push(k ? xf(ref(`I${k.ilk}`), bm.toplam.stopajli) : xn(0));
        hucreler.push(k ? xf(ref(`J${k.ilk}`), bm.toplam.kdvli) : xn(0));
        haricS.push(`${harf(c)}${r}`);
        dahilS.push(`${harf(c + 1)}${r}`);
        if (k) hakedisRef.push(ref(`L${k.ilk}`));
      });
      const cMedya = 1 + nb * 2;
      const cHizmet = cMedya + 2;
      hucreler.push(xf(haricS.join('+'), m.toplam.stopajli, XS.toplam));
      hucreler.push(xf(dahilS.join('+'), m.toplam.kdvli, XS.vurgu));
      hucreler.push(xf(hakedisRef.join('+') || '0', m.toplam.hakedis, XS.toplam));
      hucreler.push(xf(`${harf(cHizmet)}${r}*(1+${ref(M(O.kdv))})`, m.toplam.hakedis * (1 + oranlar.kdv / 100), XS.vurgu));
      mo.push(hucreler);
    });
    const gBas = 5;                                  // ilk marka satırı (1 tabanlı)
    const gSon = mo.length;                          // son marka satırı
    const grupDeger = [
      ...bloklar.flatMap((bk) => [bk.f.grup.stopajli, bk.f.grup.kdvli]),
      g.stopajli, g.kdvli, g.hakedis, g.hakedis * (1 + oranlar.kdv / 100),
    ];
    mo.push([
      xt('TOPLAM', XS.grup),
      ...grupDeger.map((v, i) => xf(`SUM(${harf(i + 1)}${gBas}:${harf(i + 1)}${gSon})`, v, XS.grup)),
    ]);
    const ws3 = {};
    mo.forEach((satir, r) => (satir || []).forEach((h, c) => { ws3[XLSX.utils.encode_cell({ r, c })] = h; }));
    ws3['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: mo.length - 1, c: mb.length - 1 } });
    ws3['!cols'] = [{ wch: 22 }, ...Array.from({ length: mb.length - 1 }, () => ({ wch: 20 }))];
    XLSX.utils.book_append_sheet(wb, ws3, 'Kesilecek faturalar');
    // Fatura sayfası ay sayfasının hemen yanında dursun.
    wb.SheetNames = [sayfaAdi, 'Kesilecek faturalar', 'Aylık özet'];

    /* 🔑 `cellFormula` olmadan formüller yazılmaz; Excel dosyayı açar açmaz
       yeniden hesaplasın diye `calcChain` da temizlenmiş sayılır. */
    XLSX.writeFile(wb, `HUPALUPA_Faturalandirma_${seciliAy}.xlsx`, { cellFormula: true, bookSST: false });
  }, [fatura, seciliAy, oranlar, aylikOzet, kismiAy]);

  /* ── Durumlar ── */

  if (yukleniyor && !veri) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Fatura verisi canlı çekiliyor…
      </div>
    );
  }

  if (hata && !veri) {
    return (
      <div className="mx-auto mt-10 max-w-lg rounded-xl bg-rose-50 p-5 ring-1 ring-rose-200">
        <div className="flex items-center gap-2 font-semibold text-rose-900">
          <AlertTriangle className="h-4 w-4" /> Sayfa yüklenemedi
        </div>
        <p className="mt-1 text-sm text-rose-800">{hata}</p>
        <button
          onClick={() => getir(true)}
          className="mt-3 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
        >
          Tekrar dene
        </button>
      </div>
    );
  }

  if (!veri || !seciliAy || !fatura) return null;

  const g = fatura.grup;
  const ikiDonem = fatura.markalar.some((m) => m.satirlar.some((x) => x.donem === 2));
  const fatura1 = ikiDonem ? altFatura(fatura, (x) => x.donem !== 2) : fatura;
  const fatura2 = ikiDonem ? altFatura(fatura, (x) => x.donem === 2) : null;
  const donemYazisi = (d) =>
    fatura.markalar.flatMap((m) => m.satirlar).find((x) => x.donem === d && x.donemEtiketi)?.donemEtiketi || '';

  return (
    /* lang="tr" ŞART: başlıklarda `uppercase` var, tarayıcı Türkçe kuralını
       bilmezse "Toplam" → "TOPLAM" derken noktasız İ basıyor. */
    <div lang="tr" className="mx-auto max-w-[1500px] px-4 py-5 print:max-w-none print:px-0">
      {/* ── Başlık ── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to="/grup/hupalupa"
            className="mb-1 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-900 print:hidden"
          >
            <ArrowLeft className="h-3 w-3" /> Patron paneline dön
          </Link>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">HUPALUPA Grubu · Faturalandırma (Kopya)</h1>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              canlı
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            Dört marka · harcama patron paneliyle aynı uçtan geliyor ·{' '}
            {veri.cached ? 'önbellekten' : `${(veri.elapsedMs / 1000).toFixed(1)} sn'de çekildi`}
            {donemBilgisi?.since ? ` · ${donemBilgisi.since} → ${donemBilgisi.until}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          <div className="flex overflow-hidden rounded-lg ring-1 ring-slate-200">
            {veri.months.map((m) => (
              <button
                key={m}
                onClick={() => setSeciliAy(m)}
                title={ayEtiketi(m)}
                className={`px-2.5 py-1.5 text-xs font-medium transition ${
                  m === seciliAy ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {ayEtiketi(m, true)}
              </button>
            ))}
          </div>
          <button
            onClick={() => getir(true)}
            disabled={yukleniyor}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${yukleniyor ? 'animate-spin' : ''}`} /> Yenile
          </button>
          <button
            onClick={excelIndir}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            <Printer className="h-3.5 w-3.5" /> Yazdır
          </button>
        </div>
      </div>

      {/* ── Oranlar ── */}
      <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-2.5 ring-1 ring-slate-200 print:hidden">
        <span className="pl-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Oranlar</span>
        <OranKutusu etiket="Google ücreti" deger={oranlar.google} ipucu="Türkiye dijital hizmet vergisi — Google faturasında tutarın içinde"
          onChange={(v) => setOranlar((o) => ({ ...o, google: v }))} />
        <OranKutusu etiket="Meta ücreti" deger={oranlar.meta} ipucu={`Konum ücreti — ${ayEtiketi(META_FEE_BASLANGIC)} ayından itibaren uygulanır`}
          onChange={(v) => setOranlar((o) => ({ ...o, meta: v }))} />
        <OranKutusu etiket="Stopaj" deger={oranlar.stopaj} ipucu="Brütleştirilir: tutar ÷ (1 − oran) − tutar"
          onChange={(v) => setOranlar((o) => ({ ...o, stopaj: v }))} />
        <OranKutusu etiket="Kredili stopaj" deger={oranlar.krediliStopaj}
          ipucu={`Kredili Meta hesapları (Store, Eğlence, Kixx), ${gunEtiketi(KREDILI_BASLANGIC)} ${KREDILI_BASLANGIC.slice(0, 4)}'den itibaren`}
          onChange={(v) => setOranlar((o) => ({ ...o, krediliStopaj: v }))} />
        <OranKutusu etiket="KDV" deger={oranlar.kdv} onChange={(v) => setOranlar((o) => ({ ...o, kdv: v }))} />
        <OranKutusu etiket="Glowy hakedişi" deger={oranlar.hakedis} ipucu="Ham harcama üzerinden, yükler bindirilmeden önce"
          onChange={(v) => setOranlar((o) => ({ ...o, hakedis: v }))} />
        {JSON.stringify(oranlar) !== JSON.stringify(VARSAYILAN_ORANLAR) && (
          <button
            onClick={() => setOranlar(VARSAYILAN_ORANLAR)}
            className="ml-auto rounded-lg px-2.5 py-1.5 text-[11px] font-medium text-slate-500 hover:bg-white hover:text-slate-900"
          >
            Varsayılana dön
          </button>
        )}
      </div>

      {/* ── Uyarılar ── */}
      {kismiAy && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <b>{ayEtiketi(seciliAy)}</b> henüz kapanmadı
            {donemBilgisi?.gecenGun && donemBilgisi?.toplamGun
              ? ` (${donemBilgisi.gecenGun}/${donemBilgisi.toplamGun} gün)`
              : ''}
            . Tutarlar ay sonuna kadar artmaya devam eder — fatura kontrolünü kapanmış ayda yapın.
          </span>
        </div>
      )}
      {yabanciParaBirimi.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            TL dışı para birimli hesap var, <b>çevrim yapılmıyor</b>:{' '}
            {yabanciParaBirimi.map((a) => `${a.marka} · ${a.name} (${a.currency})`).join(' · ')}
          </span>
        </div>
      )}
      {tumHatalar.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {tumHatalar.length} hesap okunamadı — o mecranın harcaması <b>eksik</b> olabilir:{' '}
            {tumHatalar.map((e) => `${e.marka} · ${e.account}`).join(' · ')}
          </span>
        </div>
      )}

      {/* ── Kopya sayfanın ayrımları: kredili Meta + TikTok kampanya devri ── */}
      <AyrimPaneli durum={seciliAy ? ayrimlar[seciliAy] : null} veri={veri} oranlar={oranlar} seciliAy={seciliAy} />

      {/* ── Fatura tabloları: kredili Meta ayrı faturada ── */}
      <FaturaTablosu
        fatura={fatura1}
        oranlar={oranlar}
        baslik={fatura2 ? `Fatura 1 · ${donemYazisi(1)}` : 'Fatura'}
        aciklama={fatura2
          ? `Google, Meta, TikTok · stopaj %${sayi(oranlar.stopaj, 0)} · Keramet Meta ayın tamamı`
          : seciliAy > KREDILI_BASLANGIC.slice(0, 7)
            ? `Google, TikTok stopaj %${sayi(oranlar.stopaj, 0)} · Meta (dört marka, kredili) stopaj %${sayi(oranlar.krediliStopaj, 0)}`
            : null}
        stopajBaslik={!fatura2 && seciliAy > KREDILI_BASLANGIC.slice(0, 7)
          ? `Google·TikTok %${sayi(oranlar.stopaj, 0)} · kredili Meta %${sayi(oranlar.krediliStopaj, 0)}`
          : null}
      />
      {fatura2 && (
        <FaturaTablosu
          fatura={fatura2}
          oranlar={oranlar}
          baslik={`Fatura 2 · ${donemYazisi(2)}`}
          aciklama={`Google, TikTok stopaj %${sayi(oranlar.stopaj, 0)} · kredili Meta (Store, Eğlence, KIXX) stopaj %${sayi(oranlar.krediliStopaj, 0)}`}
          stopajBaslik={`Google·TikTok %${sayi(oranlar.stopaj, 0)} · Meta %${sayi(oranlar.krediliStopaj, 0)}`}
          vurgu
        />
      )}
      {fatura2 && (
        <FaturaOzeti
          fatura1={fatura1}
          fatura2={fatura2}
          baslik1={`Fatura 1 · ${donemYazisi(1)}`}
          baslik2={`Fatura 2 · ${donemYazisi(2)}`}
          ay={seciliAy}
        />
      )}

      {/* ── Özet kartlar ── */}
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { ad: 'Mecra harcaması', v: g.harcama, alt: 'ham, yüklersiz' },
          { ad: 'Mecra ücreti', v: g.ucret, alt: `Google %${sayi(oranlar.google, 1)} + Meta %${sayi(oranlar.meta, 1)}` },
          { ad: 'Fatura toplamı', v: g.kdvli, alt: `KDV hariç ₺${sayi(g.stopajli)}`, vurgu: true },
          { ad: 'Glowy hakedişi', v: g.hakedis, alt: `harcamanın %${sayi(oranlar.hakedis, 0)}'u`, vurgu: true },
        ].map((k) => (
          <div
            key={k.ad}
            className={`rounded-xl p-3.5 ring-1 ${
              k.vurgu ? 'bg-slate-900 ring-slate-900' : 'bg-white ring-slate-200'
            }`}
          >
            <div className={`text-[11px] font-medium ${k.vurgu ? 'text-slate-300' : 'text-slate-500'}`}>{k.ad}</div>
            <div className={`mt-0.5 text-lg font-bold tabular-nums ${k.vurgu ? 'text-white' : 'text-slate-900'}`}>
              {para(k.v)}
            </div>
            <div className={`text-[11px] ${k.vurgu ? 'text-slate-400' : 'text-slate-400'}`}>{k.alt}</div>
          </div>
        ))}
      </div>

      {/* ── Marka marka toplam ─────────────────────────────────────────
          "HUPALUPA Store bu ay mecraya toplam ne harcadı, hakedişi ne oldu"
          sorusunun tek bakışta cevabı. 🔑 SEÇİLİ AYA aittir — üstteki büyük
          izgarayla aynı ay; ay düğmeleri bu tabloyu da değiştirir. */}
      <div className="mt-5 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Marka marka toplam
          </span>
          <span className="text-[11px] text-slate-400">{ayEtiketi(seciliAy)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="px-3 py-2 text-left font-semibold">Marka</th>
                {MECRALAR.map((x) => (
                  <th key={x.key} className="px-3 py-2 text-right font-medium text-slate-400">
                    {x.label}
                  </th>
                ))}
                <th className="border-l border-slate-200 px-3 py-2 text-right font-semibold">
                  Mecra harcaması
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  Fatura toplamı
                  <div className="text-[10px] font-normal text-slate-400">KDV hariç</div>
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  Fatura toplamı
                  <div className="text-[10px] font-normal text-slate-400">KDV dahil</div>
                </th>
                <th className="border-l border-slate-200 px-3 py-2 text-right font-semibold">
                  Glowy hakedişi
                </th>
              </tr>
            </thead>
            <tbody>
              {fatura.markalar.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className="inline-flex items-center gap-2">
                      {m.logo && (
                        <img
                          src={m.logo}
                          alt=""
                          className="h-5 w-5 shrink-0 rounded object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <span className="font-semibold text-slate-900">{m.label}</span>
                    </span>
                  </td>
                  {MECRALAR.map((x) => {
                    const v = mecraHarcamasi(m, x.key);
                    return (
                      <td key={x.key} className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-500">
                        {v ? sayi(v) : '—'}
                      </td>
                    );
                  })}
                  <td className="whitespace-nowrap border-l border-slate-200 bg-amber-50/60 px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-900">
                    {sayi(m.toplam.harcama)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-slate-500">
                    {sayi(m.toplam.stopajli)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                    {sayi(m.toplam.kdvli)}
                  </td>
                  <td className="whitespace-nowrap border-l border-slate-200 bg-amber-50/60 px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-900">
                    {sayi(m.toplam.hakedis)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-900 text-white">
                <td className="px-3 py-2.5 font-bold uppercase tracking-wide">Grup</td>
                {MECRALAR.map((x) => (
                  <td key={x.key} className="px-3 py-2.5 text-right tabular-nums text-slate-300">
                    {sayi(fatura.markalar.reduce((t, m) => t + mecraHarcamasi(m, x.key), 0))}
                  </td>
                ))}
                <td className="border-l border-slate-700 px-3 py-2.5 text-right text-sm font-bold tabular-nums">
                  {sayi(g.harcama)}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-300">
                  {sayi(g.stopajli)}
                </td>
                <td className="px-3 py-2.5 text-right font-bold tabular-nums">{sayi(g.kdvli)}</td>
                <td className="border-l border-slate-700 px-3 py-2.5 text-right text-sm font-bold tabular-nums">
                  {sayi(g.hakedis)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Aylık seyir ── */}
      <div className="mt-5 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        <div className="border-b border-slate-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ay ay grup toplamı
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="px-3 py-2 text-left font-semibold">Ay</th>
                <th className="px-3 py-2 text-right font-semibold">Harcama</th>
                <th className="px-3 py-2 text-right font-semibold">Mecra ücreti</th>
                <th className="px-3 py-2 text-right font-semibold">Fatura toplamı</th>
                <th className="px-3 py-2 text-right font-semibold">Glowy hakedişi</th>
              </tr>
            </thead>
            <tbody>
              {aylikOzet.map((r) => (
                <tr
                  key={r.ay}
                  onClick={() => setSeciliAy(r.ay)}
                  className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${
                    r.ay === seciliAy ? 'bg-slate-50 font-semibold' : ''
                  }`}
                >
                  <td className="px-3 py-1.5 text-slate-700">{ayEtiketi(r.ay)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{sayi(r.harcama)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{sayi(r.ucret)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-900">{sayi(r.kdvli)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{sayi(r.hakedis)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── 🔶 GEÇİCİ · Keramet indirim simülasyonu ─────────────────────
          Efe, 2 Eyl 2026: bu ay Keramet Ilıcası'na %50 daha az fatura
          kesilecek. 🔴🔴 Yalnızca BU blok simüledir — yukarıdaki tablolar,
          özet kartlar ve Excel çıktısı canlı harcamayı gösterir. İş bitince
          blok komple silinecek. */}
      {simulasyon && (
        <div className="mt-5 overflow-hidden rounded-xl bg-amber-50/60 ring-2 ring-dashed ring-amber-300">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 px-4 py-2.5">
            <div>
              <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                Geçici · simülasyon
              </span>
              <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-amber-900">
                {simulasyon.marka.label} — %{sayi(simIndirim, 0)} daha az fatura
              </span>
              <div className="mt-0.5 text-[11px] text-amber-800">
                {ayEtiketi(seciliAy)} · harcama %{sayi(simIndirim, 0)} azalmış gibi hesaplandı.
                Yukarıdaki tablolar ve Excel <b>değişmedi</b>, orada gerçek harcama var.
              </div>
            </div>
            <label className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-amber-300">
              <span className="text-[11px] font-medium text-amber-800">İndirim</span>
              <span className="text-[11px] text-amber-500">%</span>
              <input
                type="number"
                step="5"
                min="0"
                max="100"
                value={simIndirim}
                onChange={(e) => setSimIndirim(Number(e.target.value))}
                className="w-14 rounded border-0 bg-amber-50 px-1.5 py-0.5 text-right text-xs font-semibold tabular-nums text-amber-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
            </label>
          </div>

          <div className="overflow-x-auto bg-white">
            <table className="w-full min-w-[900px] border-collapse text-xs">
              <thead>
                <tr className="bg-amber-100/70 text-amber-900">
                  <th className="px-3 py-2 text-left font-semibold">Mecra</th>
                  <th className="px-3 py-2 text-right font-medium text-amber-700">Gerçek harcama</th>
                  <th className="border-l border-amber-200 px-3 py-2 text-right font-semibold">
                    Simüle harcama
                    <div className="text-[10px] font-normal text-amber-700">
                      × {sayi(simulasyon.carpan, 2)}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">Mecra Ücreti Dahil</th>
                  <th className="px-3 py-2 text-right font-semibold">Stopaj %{sayi(oranlar.stopaj, 0)}</th>
                  <th className="px-3 py-2 text-right font-semibold">Stopaj Dahil</th>
                  <th className="px-3 py-2 text-right font-semibold">KDV %{sayi(oranlar.kdv, 0)}</th>
                  <th className="px-3 py-2 text-right font-semibold">KDV Dahil</th>
                </tr>
              </thead>
              <tbody>
                {simulasyon.satirlar.map((s) => (
                  <tr key={s.anahtar} className="border-t border-amber-100">
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        <img
                          src={s.mecra.logo}
                          alt=""
                          className="h-3.5 w-3.5 object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className="text-slate-700">{s.mecra.label}</span>
                      </span>
                    </td>
                    <P v={s.mevcut} soluk />
                    <P v={s.harcama} guclu sinif="border-l border-amber-200 bg-amber-50/70" />
                    <P v={s.ucretli} />
                    <P v={s.stopaj} />
                    <P v={s.stopajli} />
                    <P v={s.kdv} />
                    <P v={s.kdvli} guclu />
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-amber-900 text-white">
                  <td className="px-3 py-2.5 font-bold uppercase tracking-wide">Toplam</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-200">
                    {sayi(simulasyon.marka.toplam.harcama)}
                  </td>
                  <td className="border-l border-amber-700 px-3 py-2.5 text-right font-bold tabular-nums">
                    {sayi(simulasyon.toplam.harcama)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.ucretli)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.stopaj)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.stopajli)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.kdv)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums">
                    {sayi(simulasyon.toplam.kdvli)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Kesilecek rakamlar — gerçeğiyle yan yana. */}
          <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-3">
            {[
              {
                ad: 'Kesilecek fatura · KDV hariç',
                v: simulasyon.toplam.stopajli,
                mevcut: simulasyon.marka.toplam.stopajli,
                vurgu: true,
              },
              {
                ad: 'Kesilecek fatura · KDV dahil',
                v: simulasyon.toplam.kdvli,
                mevcut: simulasyon.marka.toplam.kdvli,
                vurgu: true,
              },
              {
                ad: `Glowy hakedişi · %${sayi(oranlar.hakedis, 0)}`,
                v: simulasyon.toplam.hakedis,
                mevcut: simulasyon.marka.toplam.hakedis,
              },
            ].map((k) => (
              <div
                key={k.ad}
                className={`rounded-xl p-3.5 ring-1 ${
                  k.vurgu ? 'bg-amber-900 text-white ring-amber-900' : 'bg-white ring-amber-200'
                }`}
              >
                <div className={`text-[11px] font-medium ${k.vurgu ? 'text-amber-200' : 'text-slate-500'}`}>
                  {k.ad}
                </div>
                <div className="mt-0.5 text-lg font-bold tabular-nums">{para(k.v)}</div>
                <div className={`text-[11px] tabular-nums ${k.vurgu ? 'text-amber-300' : 'text-slate-400'}`}>
                  gerçeği {para(k.mevcut)} · {para(Math.abs(k.mevcut - k.v))} daha az
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Okuma notları ── */}
      <div className="mt-4 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <Info className="h-3.5 w-3.5" /> Nasıl hesaplanıyor
        </div>
        <ul className="mt-2 space-y-1.5 text-[11px] leading-relaxed text-slate-600">
          <li>
            <b>Merdiven:</b> harcama × (1 + mecra ücreti) → ÷ (1 − stopaj) → × (1 + KDV). Mecra ücreti
            stopaj matrahının <b>içindedir</b>.
          </li>
          <li>
            <b>Stopaj brütleştirmedir</b>, düz çarpım değil: tutar ÷ {sayi(1 - oranlar.stopaj / 100, 2)} − tutar.
            ×{sayi(1 + oranlar.stopaj / 100, 2)} yazmak kalem başına ~%{sayi(((1 / (1 - oranlar.stopaj / 100)) - (1 + oranlar.stopaj / 100)) * 100, 2)} eksik verir.
          </li>
          <li>
            <b>Glowy hakedişi ham harcama üzerinden</b> hesaplanır — vergi, stopaj ve mecra ücreti
            bindirilmeden önce.
          </li>
          <li>
            <b>Meta konum ücreti %{sayi(oranlar.meta, 1)}</b> yalnızca {ayEtiketi(META_FEE_BASLANGIC)} ve sonrası
            için uygulanır; öncesindeki aylarda Meta satırında ücret sütunu boştur. Google'ın %{sayi(oranlar.google, 1)}'i
            Türkiye dijital hizmet vergisidir ve her ayda geçerlidir. TikTok'a mecra ücreti binmez.
          </li>
          <li>
            Harcama rakamları Meta / Google Ads / TikTok API'lerinden <b>canlı</b> okunur (patron paneliyle
            aynı uç, aynı önbellek). Yüklerin <b>hiçbiri API'den gelmez</b>, hepsi bu sayfada bindirilir.
          </li>
          <li>Oranlar bu ekranda geçici olarak değiştirilebilir; <b>kaydedilmez</b>, sayfa yenilenince varsayılana döner.</li>
        </ul>
      </div>
    </div>
  );
}
